import brandingConfig from '../config/signInBranding.json';
import { MAIL_DOORS } from './authMailDoor';

// Brand strings live in src/config/signInBranding.json; the doors only pick which tenant's set to wear.
const platformBrand = brandingConfig.brands.platform;
const rideBrand = brandingConfig.brands['the-ride-atl'];

export const SIGN_IN_DOORS = [
    {
        slug: null,
        path: '/',
        company: 'SmarTEKnowledgy',
        entityId: null,
        brand: { brand_name: platformBrand.name, logo_url: null, accent: null, slug: null },
        identity: platformBrand,
        manifest: 'push-app.webmanifest',
        hostManifest: 'push-app.webmanifest',
        icon: 'icons/intelagent-180.png',
        // Browser-tab icon: the four-panel mark. Every page of the door, signed in or out.
        favicons: { ico: 'favicons/intelagent/favicon.ico', png: 'favicons/intelagent/favicon-32.png', svg: 'favicons/intelagent/favicon.svg' },
        signInLogo: null,
        hostnames: brandingConfig.hostnames.platform,
    },
    {
        slug: 'the-ride-atl',
        path: '/the-ride-atl',
        company: 'The Ride: ATL',
        entityId: brandingConfig.companyEntityId,
        brand: { brand_name: rideBrand.name, logo_url: null, accent: null, slug: 'the-ride-atl' },
        identity: rideBrand,
        // macOS app names cannot contain ":" (Safari's Add to Dock turns "The Ride: ATL" into "The Ride- ATL"), so the INSTALLED app is named with a
        // middle dot. Everything else (titles, emails, texts, alert titles, UI copy) keeps "The Ride: ATL".
        installedName: 'The Ride · ATL',
        manifest: 'push-app-the-ride-atl.webmanifest',
        // On its own address the door IS the site root, so the Home Screen icon opens app.therideatl.com/, not /the-ride-atl.
        hostManifest: 'push-app-the-ride-atl-host.webmanifest',
        icon: 'icons/the-ride-atl-180.png',
        // Browser-tab icon: the real logo, white on black (raster art, so no SVG). Never the IntelAgent mark.
        favicons: { ico: 'favicons/the-ride-atl/favicon.ico', png: 'favicons/the-ride-atl/favicon-32.png', svg: null },
        // Small copy of the real logo for the sign-in pages; the full-size original is still used elsewhere.
        signInLogo: 'icons/the-ride-atl-logo-384.png',
        hostnames: brandingConfig.hostnames['the-ride-atl'],
    },
];

// The client app (book.therideatl.com) is its own site; this list only lets other code recognise its address.
export const CLIENT_APP_HOSTNAMES = brandingConfig.hostnames.clientApp;

export const ENABLE_SIGN_IN_SSO = false;

export function doorForSlug(slug) {
    if (!slug) return null;
    return SIGN_IN_DOORS.find((door) => door.slug === slug) || null;
}

export function doorForAddress({ pathname = '/', hostname = '' } = {}) {
    const host = hostname.toLowerCase().split(':')[0];
    const byHost = SIGN_IN_DOORS.find((door) => door.hostnames.some((name) => name.toLowerCase() === host));
    if (byHost) return byHost;

    return [...SIGN_IN_DOORS]
        .filter((door) => door.slug && (pathname === door.path || pathname.startsWith(`${door.path}/`)))
        .sort((left, right) => right.path.length - left.path.length)[0] || SIGN_IN_DOORS[0];
}

export function legacyBrandRedirectLocation(location) {
    const params = new URLSearchParams(location.search || '');
    const door = doorForSlug(params.get('brand'));
    if (!door) return null;

    params.delete('brand');
    const remainingSearch = params.toString();
    const currentPath = location.pathname || '/';
    const alreadyAtDoor = door.path !== '/'
        && (currentPath === door.path || currentPath.startsWith(`${door.path}/`));
    const relativePath = alreadyAtDoor
        ? currentPath.slice(door.path.length).replace(/^\/+/, '')
        : currentPath === '/' ? '' : currentPath.replace(/^\/+/, '');
    const path = `${door.path}${relativePath ? `/${relativePath}` : ''}`;
    return {
        pathname: path,
        search: remainingSearch ? `?${remainingSearch}` : '',
        hash: location.hash || '',
    };
}

export function doorBasePath({ pathname = '/', hostname = '' } = {}) {
    const door = doorForAddress({ pathname, hostname });
    const host = hostname.toLowerCase().split(':')[0];
    return door.slug && !door.hostnames.some((name) => name.toLowerCase() === host) ? door.path : '/';
}

export function doorLink(targetDoor, location = window.location) {
    const currentDoor = doorForAddress(location);
    const hostname = location.hostname.toLowerCase().split(':')[0];
    const onHostDoor = currentDoor.hostnames.some((name) => name.toLowerCase() === hostname);
    // A door reached by its own address is not a path under another door's address, so link to the target's own address.
    if (onHostDoor && targetDoor.hostnames.length) return `https://${targetDoor.hostnames[0]}/`;
    const origin = onHostDoor ? brandingConfig.commandCenterOrigin : location.origin;
    return `${origin}${targetDoor.path}`;
}

// The person's own door, always at its canonical address, e.g. https://app.therideatl.com/sign-in.
// expectedDoor comes from that person's own account (never another tenant's), so no other door is ever named.
export function wrongDoorLink(expectedDoor) {
    return `${MAIL_DOORS[expectedDoor.slug ?? 'platform'].origin}/sign-in`;
}

export function wrongDoorMessage(currentDoor, expectedDoor, href = wrongDoorLink(expectedDoor)) {
    const expectedName = MAIL_DOORS[expectedDoor.slug ?? 'platform'].name;
    return {
        heading: 'Access denied',
        text: "You've been signed out. Please contact your admin if you believe this is an error.",
        intro: 'Your account is set up here:',
        href,
        linkText: `Sign in at ${expectedName} \u2192`,
    };
}

// options.multiCompany (BL-408 G2): a person with two or more companies who is inside one of them (chosen by this sign-in
// session, by the door, or from "Choose a company") may use any door they reached; which company they see is the server's
// choice, not the door's. Checked only where a one-company person would be refused, so their answer is unchanged.
export async function checkDoorAccess(door, supabaseClient, userId, options = {}) {
    const [
        { data: platformRole, error: adminError },
        { data: appAccess, error: appAccessError },
        { data: signedInBrand, error: brandError },
    ] =
        await Promise.all([
            supabaseClient.rpc('bl407_platform_role'),
            supabaseClient.rpc('bl391_app_access'),
            supabaseClient.rpc('bl402_signin_welcome'),
        ]);
    if (adminError) throw adminError;
    if (appAccessError) throw appAccessError;
    if (brandError) throw brandError;
    if (['platform_owner', 'super_admin', 'platform_admin'].includes(platformRole)) {
        const { data: context, error } = await supabaseClient.rpc('bl407_door_context', { p_slug: door.slug });
        if (error) throw error;
        return { allowed: true, context };
    }
    if (appAccess?.command_center_enabled === false && appAccess?.driver_app_enabled === true) {
        return { allowed: true, driverOnly: true };
    }

    const { data: membership, error: membershipError } = await supabaseClient
        .from('org_members')
        .select('org_id')
        .eq('user_id', userId)
        .maybeSingle();
    if (membershipError) throw membershipError;

    const hasCommandCenterAccess = appAccess?.command_center_enabled === true;
    const expectedDoor = signedInBrand?.slug ? doorForSlug(signedInBrand.slug) : null;
    const hasMatchingDoor = Boolean(membership && expectedDoor?.slug === door.slug && hasCommandCenterAccess);
    if (options.multiCompany && membership && hasCommandCenterAccess
        && !(door.slug ? hasMatchingDoor : !expectedDoor)
        && await options.multiCompany()) {
        return { allowed: true };
    }
    if (door.slug) {
        return hasMatchingDoor
            ? { allowed: true }
            : {
                allowed: false,
                expectedDoor: expectedDoor && expectedDoor.slug !== door.slug
                    ? expectedDoor
                    : membership && hasCommandCenterAccess ? SIGN_IN_DOORS[0] : null,
            };
    }

    if (hasCommandCenterAccess && expectedDoor) return { allowed: false, expectedDoor };
    if (hasCommandCenterAccess && membership) return { allowed: true };
    return { allowed: false, expectedDoor: null };
}
